import styled from '@emotion/styled';
import { FormikValues, useField, useFormikContext } from 'formik';
import React from 'react';
import { SoloInput } from './SoloInput';
import { colors } from 'styles/colors';
import { RemoveTagButton } from './SoloButton';
import { ReactComponent as CloseIcon } from 'assets/rounded-close-icon.svg';
import { ReactComponent as GreenPlus } from 'assets/small-green-plus.svg';

type ErrorTextProps = { errorExists?: boolean };

export const ErrorText = styled.div`
  color: ${colors.grapefruitOrange};
  visibility: ${(props: ErrorTextProps) => (props.errorExists ? 'visible' : 'hidden')};
  height: 15px;
  margin-top: 5px;
`;

type FormInputProps = {
  label: string;
  name: string;
};

export const FormInput: React.FC<FormInputProps> = ({ label, ...props }) => {
  const [field, meta] = useField(props.name);

  return (
    <div>
      <label className='block text-sm font-medium leading-5 text-gray-700'>{label}</label>
      <div className='relative mt-1 rounded-md shadow-sm'>
        <input
          className='block w-full pr-10 text-red-900 placeholder-red-300 border-red-300 form-input focus:border-red-300 focus:shadow-outline-red sm:text-sm sm:leading-5'
          {...field}
          {...props}
        />
        <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
          <svg className='w-5 h-5 text-red-500' fill='currentColor' viewBox='0 0 20 20' aria-hidden='true'>
            <path
              fillRule='evenodd'
              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
              clipRule='evenodd'
            />
          </svg>
        </div>
      </div>
      {meta.error && meta.touched && <p className='mt-2 text-sm text-red-600'>{meta.error}</p>}
    </div>
  );
};

export const SoloFormInput = ({ ...props }) => {
  const { hideError = false } = props;
  const form = useFormikContext<any>();
  const field = form.getFieldProps(props.name);
  const meta = form.getFieldMeta(props.name);

  return (
    <>
      <SoloInput
        {...field}
        {...props}
        error={!!meta.error && meta.touched}
        title={props.title}
        value={field.value}
        onChange={field.onChange}
      />
      {hideError ? null : <ErrorText errorExists={!!meta.error && meta.touched}>{meta.error}</ErrorText>}
    </>
  );
};

export type SoloFormMetadataProps = {
  name: string; // the name of this field in Formik
  newMetadataKey?: string;
  newMetadataValue?: string;
  labels?: [string, string][];
  editMode?: boolean;
};

export const SoloFormMetadata = <Values extends FormikValues>(props: SoloFormMetadataProps) => {
  const { name, editMode = true, newMetadataKey = 'newKey', newMetadataValue = 'newValue', labels } = props;
  const { values, setFieldValue } = useFormikContext<Values>();

  const newLabelIsAddable =
    !!values[newMetadataKey].length &&
    !!values[newMetadataValue].length &&
    !(values[name] as [string, string][]).some(
      ([existingLabel, existingValue]) => values[newMetadataKey] === existingLabel
    );

  return (
    <>
      <div className='text-base font-medium text-gray-900'>Custom Metadata</div>
      {editMode ? (
        <>
          <div className='flex flex-col '>
            <div className='flex flex-wrap items-center justify-start gap-2 '>
              {(values[name] as [string, string][])?.map(([labelKey, labelValue]) => (
                <span
                  key={`${labelKey}:${labelValue}`}
                  className='inline-flex items-center pl-2 pr-0.5 py-0.5 rounded-full text-sm font-medium bg-blue-200gloo text-blue-600gloo  '>
                  <span>
                    <span className='text-gray-900 '> {labelKey}</span> :
                    <span className='mr-1 font-normal text-gray-700 '> {labelValue}</span>
                  </span>
                  <RemoveTagButton type='button' aria-label='Remove tag'>
                    <div
                      onClick={() => {
                        let newLabels = (values[name] as [string, string][]).filter(([k, v]) => labelKey !== k);
                        setFieldValue(name, newLabels);
                      }}>
                      <span className='text-blue-700gloo'>
                        <CloseIcon title={`Click to remove ${labelKey} : ${labelValue}`} />
                      </span>
                    </div>
                  </RemoveTagButton>
                </span>
              ))}
            </div>
            <div className='grid justify-center grid-rows-1 mt-2'>
              <div className='flex items-center col-span-3'>
                <span className='flex items-center space-x-1'>
                  <SoloFormInput name={newMetadataKey} placeholder='Metadata Name' />
                  <SoloFormInput name={newMetadataValue} placeholder='Metadata Value' />
                </span>

                <span
                  className={`flex items-center mx-0.5 ${
                    newLabelIsAddable ? 'text-green-400 hover:text-green-300 cursor-pointer' : 'text-gray-600'
                  }`}
                  onClick={() => {
                    if (newLabelIsAddable) {
                      let newLabels = [
                        ...(values[name] as [string, string][]),
                        [values[newMetadataKey], values[newMetadataValue]]
                      ];
                      setFieldValue(name, newLabels);
                      setFieldValue(newMetadataKey, '');
                      setFieldValue(newMetadataValue, '');
                    }
                  }}>
                  <GreenPlus className='mr-1 fill-current' title='Click to add this new label' />
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className='flex items-center space-x-1'>
          {(labels ?? (values[name] as [string, string][])).map(([labelKey, labelValue]) => (
            <span
              key={`${labelKey}:${labelValue}`}
              className='inline-flex items-center pl-2 pr-0.5 py-0.5 rounded-full text-sm font-medium bg-blue-200gloo text-blue-600gloo  '>
              <span>
                <span className='text-gray-900 '> {labelKey}</span> :
                <span className='mr-1 font-normal text-gray-700 '> {labelValue}</span>
              </span>
            </span>
          ))}
        </div>
      )}
    </>
  );
};
